import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Upload, Check, Trash2, FileText, Printer, Pencil } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { money, outletNames, formatDate } from "../lib/format";
import { PageIntro, PanelHead, Modal, Field, Badge, Pagination } from "../components/UI";
import { usePagination } from "../lib/usePagination";
import { BulkUploadDialog } from "../components/BulkUpload";
import { useAuth } from "../context/AuthContext";
import { useOutlets } from "../lib/useOutlets";
import { printGRN } from "../lib/printDocs";

const TEMPLATE = {
  headers: ["po_number", "item_sku", "qty", "price", "notes"],
  example: [
    ["PO-0001", "PRT-0001", "20", "185000", "Morning delivery"],
    ["PO-0001", "PRT-0002", "5", "225000", ""],
  ],
};

export default function ReceivingPage() {
  const { user } = useAuth();
  const outlets = useOutlets();
  const [list, setList] = useState([]);
  const [approvedPOs, setApprovedPOs] = useState([]);
  const [modal, setModal] = useState(null); // "new" | "upload" | null
  const [selectedPO, setSelectedPO] = useState(null); // full PO object
  const [destOutlet, setDestOutlet] = useState(""); // user-editable destination outlet
  const [notes, setNotes] = useState("");
  const [docType, setDocType] = useState("Invoice");
  const [docNumber, setDocNumber] = useState("");
  const [docDate, setDocDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [lines, setLines] = useState([]); // {item_id, name, unit, qty, price}
  const [saving, setSaving] = useState(false);

  const canCreate = ["admin", "warehouse"].includes(user?.role);
  const canEditDocs = ["admin", "purchasing", "finance", "warehouse"].includes(user?.role);
  const pager = usePagination(list, 100, "all");
  const [docEdit, setDocEdit] = useState(null); // grn being edited
  const [invNo, setInvNo] = useState("");
  const [doNo, setDoNo] = useState("");
  const [savingDoc, setSavingDoc] = useState(false);

  const openDocEdit = (g) => {
    setDocEdit(g);
    setInvNo(g.invoice_number || (g.document_type !== "Delivery Order" ? g.document_number || "" : ""));
    setDoNo(g.delivery_order_number || (g.document_type === "Delivery Order" ? g.document_number || "" : ""));
  };
  const submitDocEdit = async (e) => {
    e.preventDefault();
    setSavingDoc(true);
    try {
      await api.patch(`/receivings/${docEdit.id}/document-numbers`, {
        invoice_number: invNo, delivery_order_number: doNo,
      });
      toast.success("Document numbers updated");
      setDocEdit(null);
      loadList();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingDoc(false);
    }
  };

  const loadList = () => api.get("/receivings").then((r) => setList(r.data));  const loadPOs = () =>
    api.get("/orders").then((r) => {
      // Allow both approved (not yet received) and partially-received POs
      setApprovedPOs(r.data.filter((o) => o.status === "approved" || o.status === "partial"));
    });

  useEffect(() => {
    loadList();
    loadPOs();
  }, []);

  const openNew = () => {
    if (approvedPOs.length === 0) {
      toast.error("No PO ready to receive. Approve a PO first.");
      return;
    }
    setSelectedPO(null);
    setLines([]);
    setNotes("");
    setDocType("Invoice");
    setDocNumber("");
    setDocDate(new Date().toISOString().slice(0, 10));
    setModal("new");
  };

  const onPickPO = (poId) => {
    const po = approvedPOs.find((p) => p.id === poId);
    setSelectedPO(po);
    // Default destination outlet to the PO's outlet — user can override it.
    setDestOutlet(po?.outlet_code || "main_wh");
    if (po) {
      setLines(
        po.items.map((l) => {
          const ordered = Number(l.qty);
          const received = Number(l.received_qty || 0);
          const remaining = ordered - received;
          // Allow up to 20% over the ordered qty
          const maxReceivable = Math.max(0, ordered * 1.2 - received);
          return {
            item_id: l.item_id,
            name: l.name,
            unit: l.unit,
            ordered,
            received,
            remaining,
            maxReceivable,
            qty: String(remaining > 0 ? remaining : 0),
            price: String(l.price),
          };
        }).filter((l) => l.maxReceivable > 0)
      );
    } else {
      setLines([]);
    }
  };

  const updateLine = (idx, field, value) => {
    const next = [...lines];
    next[idx] = { ...next[idx], [field]: value };
    setLines(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedPO) return toast.error("Select a Purchase Order first");
    if (!docNumber.trim()) return toast.error("Document number (Invoice / Delivery Order No.) is required");
    const cleanLines = lines.filter((l) => Number(l.qty) > 0);
    if (cleanLines.length === 0) return toast.error("At least 1 item must be received");
    setSaving(true);
    try {
      await api.post("/receivings", {
        po_id: selectedPO.id,
        supplier: selectedPO.supplier,
        outlet_code: destOutlet || selectedPO.outlet_code,
        notes,
        document_type: docType,
        document_number: docNumber.trim(),
        document_date: docDate,
        items: cleanLines.map((l) => ({
          item_id: l.item_id,
          name: l.name,
          qty: Number(l.qty),
          unit: l.unit,
          price: Number(l.price),
        })),
      });
      toast.success("Receiving recorded & COGS auto-updated");
      setModal(null);
      loadList();
      loadPOs();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const linesTotal = lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.price || 0), 0);

  return (
    <>
      <PageIntro
        eyebrow="Operations · goods receiving"
        title="Goods Received Note (GRN)"
        subtitle="Every receiving must reference an approved Purchase Order. Weighted-average COGS is auto-updated."
        testid="receiving-title"
        action={
          <div className="action-cluster">
            {canCreate && (
              <button
                data-testid="receiving-upload-button"
                className="secondary-button"
                onClick={() => setModal("upload")}
              >
                <Upload size={16} /> Upload CSV
              </button>
            )}
            {canCreate && (
              <button
                data-testid="receiving-primary-action"
                className="primary-button"
                onClick={openNew}
              >
                <Plus size={17} /> Receive goods
              </button>
            )}
          </div>
        }
      />

      {canCreate && approvedPOs.length > 0 && (
        <div className="info-strip" data-testid="approved-po-banner">
          <FileText size={16} />
          <span>
            <strong>{approvedPOs.length}</strong> Purchase Orders ready to receive
          </span>
          <button className="text-button" onClick={openNew}>
            Process now ›
          </button>
        </div>
      )}
      {canCreate && approvedPOs.length === 0 && (
        <div className="info-strip warning" data-testid="no-po-banner">
          <FileText size={16} />
          <span>No approved Purchase Order. Receiving cannot proceed without a PO.</span>
        </div>
      )}

      <section className="panel">
        <PanelHead title="Receiving history" detail={`${list.length} GRN recorded`} />
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>GRN No.</th>
                <th>PO Ref</th>
                <th>Document</th>
                <th>Supplier</th>
                <th>Outlet</th>
                <th>Lines</th>
                <th>Total</th>
                <th>Received</th>
                <th>By</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && (
                <tr><td colSpan={10} style={{ textAlign: "center", padding: 40 }}>No receivings yet.</td></tr>
              )}
              {pager.pageItems.map((g) => (
                <tr key={g.id} data-testid={`grn-row-${g.id}`}>
                  <td><strong>{g.grn_number}</strong></td>
                  <td>
                    {g.po_number ? <Badge tone="blue">{g.po_number}</Badge> : <small>-</small>}
                  </td>
                  <td data-testid={`grn-doc-${g.id}`}>
                    {g.document_number ? (
                      <>
                        <strong>{g.document_number}</strong>
                        <small>{g.document_type || "Invoice"}{g.document_date ? ` · ${g.document_date}` : ""}</small>
                      </>
                    ) : (
                      <Badge tone="neutral">Legacy</Badge>
                    )}
                  </td>
                  <td>{g.supplier}</td>
                  <td>
                    {outletNames[g.outlet_code] || g.outlet_code}
                    {g.is_direct_cost && <small><Badge tone="amber">Direct cost</Badge></small>}
                  </td>
                  <td>
                    {g.items.length} item · {g.items.reduce((s, l) => s + l.qty, 0)} qty
                  </td>
                  <td><strong>{money(g.total)}</strong></td>
                  <td><small>{formatDate(g.received_at)}</small></td>
                  <td><small>{g.received_by}</small></td>
                  <td>
                    <button
                      data-testid={`grn-print-${g.id}`}
                      className="small-button"
                      onClick={() => printGRN(g)}
                      title="Print GRN"
                    >
                      <Printer size={12} /> Print
                    </button>
                    {canEditDocs && (g.status || "normal").toLowerCase() === "normal" && (
                      <button
                        data-testid={`grn-edit-docs-${g.id}`}
                        className="small-button"
                        style={{ marginLeft: 6 }}
                        onClick={() => openDocEdit(g)}
                        title="Edit invoice / delivery order number"
                      >
                        <Pencil size={12} /> Edit No.
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={pager.page} pageCount={pager.pageCount} total={pager.total} pageSize={pager.pageSize} onChange={pager.setPage} testid="receiving-pagination" />
      </section>

      {modal === "new" && (
        <Modal title="Receive goods from PO" onClose={() => setModal(null)}>
          <form onSubmit={submit}>
            <label className="field">
              <span>Purchase Order being received</span>
              <select
                data-testid="grn-po-select"
                value={selectedPO?.id || ""}
                onChange={(e) => onPickPO(e.target.value)}
                required
              >
                <option value="">-- Select approved PO --</option>
                {approvedPOs.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.po_number} · {p.supplier} · {outletNames[p.outlet_code] || p.outlet_code} · {money(p.total)}
                  </option>
                ))}
              </select>
            </label>

            {selectedPO && (
              <>
                <div className="po-summary" data-testid="po-summary">
                  <div>
                    <span>Supplier</span>
                    <strong>{selectedPO.supplier}</strong>
                  </div>
                  <div>
                    <span>Destination outlet (editable)</span>
                    <select
                      data-testid="grn-destination-outlet-select"
                      value={destOutlet}
                      onChange={(e) => setDestOutlet(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "6px 8px",
                        border: "1px solid var(--line)",
                        borderRadius: 5,
                        font: "13px 'DM Sans'",
                      }}
                    >
                      {outlets.map((o) => (
                        <option key={o.code} value={o.code}>
                          {o.name}{o.type === "warehouse" ? " (stock)" : " (direct cost)"}
                        </option>
                      ))}
                    </select>
                    {destOutlet && destOutlet !== "main_wh" && (
                      <small style={{ marginTop: 4, display: "block" }}>
                        <Badge tone="amber">Direct cost — not warehouse stock</Badge>
                      </small>
                    )}
                    {destOutlet === "main_wh" && selectedPO.outlet_code && selectedPO.outlet_code !== "main_wh" && (
                      <small style={{ marginTop: 4, display: "block" }}>
                        <Badge tone="neutral">PO was for {outletNames[selectedPO.outlet_code] || selectedPO.outlet_code} — you changed destination to warehouse</Badge>
                      </small>
                    )}
                  </div>
                  <div>
                    <span>PO value</span>
                    <strong>{money(selectedPO.total)}</strong>
                  </div>
                </div>

                <p className="eyebrow" style={{ marginTop: 18 }}>
                  Supplier document (required for Xero Bills)
                </p>
                <div className="form-grid" data-testid="grn-doc-fields">
                  <label className="field">
                    <span>Document type</span>
                    <select
                      data-testid="grn-document-type-select"
                      value={docType}
                      onChange={(e) => setDocType(e.target.value)}
                    >
                      <option value="Invoice">Invoice</option>
                      <option value="Delivery Order">Delivery Order</option>
                    </select>
                  </label>
                  <Field
                    label="Document number (required)"
                    testid="grn-document-number-input"
                    value={docNumber}
                    onChange={setDocNumber}
                    placeholder="e.g. INV-2026-0012 / DO-8891"
                    required
                  />
                  <Field
                    label="Document date"
                    testid="grn-document-date-input"
                    type="date"
                    value={docDate}
                    onChange={setDocDate}
                  />
                </div>

                <p className="eyebrow" style={{ marginTop: 18 }}>
                  Line items — remaining (not yet received) qty is prefilled. Up to <b>20% over</b> the
                  ordered qty per line is accepted.
                </p>
                <div className="line-editor">
                  {lines.length === 0 && (
                    <div className="empty-hint">All items on this PO have been fully received.</div>
                  )}
                  {lines.map((l, idx) => (
                    <div className="line-row partial" key={idx}>
                      <div className="line-item-label">
                        <strong>{l.name}</strong>
                        <small>
                          Ordered {l.ordered} · Received so far {l.received} ·
                          Remaining <b>{l.remaining}</b> {l.unit} ·
                          Max (with 20% tolerance) <b>{Number(l.maxReceivable).toFixed(2)}</b>
                        </small>
                      </div>
                      <input
                        data-testid={`grn-line-qty-${idx}`}
                        type="number"
                        max={l.maxReceivable}
                        min="0"
                        step="0.01"
                        value={l.qty}
                        onChange={(e) => updateLine(idx, "qty", e.target.value)}
                        placeholder="Qty received"
                      />
                      <input
                        data-testid={`grn-line-price-${idx}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={l.price}
                        onChange={(e) => updateLine(idx, "price", e.target.value)}
                        placeholder="Price"
                      />
                      <div />
                    </div>
                  ))}
                </div>
                <div className="line-total">Total received: <strong>{money(linesTotal)}</strong></div>

                <label className="field" style={{ marginTop: 12 }}>
                  <span>Receiving notes</span>
                  <input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Delivery order #..., item condition, etc."
                  />
                </label>
              </>
            )}

            <div className="form-actions">
              <button type="button" className="secondary-button" onClick={() => setModal(null)}>Cancel</button>
              <button
                data-testid="grn-save-button"
                className="primary-button"
                type="submit"
                disabled={!selectedPO || saving}
              >
                <Check size={16} /> {saving ? "Saving..." : "Save receiving"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {docEdit && (
        <Modal title={`Edit document numbers · ${docEdit.grn_number}`} onClose={() => setDocEdit(null)}>
          <form className="form-grid" onSubmit={submitDocEdit}>
            <p className="form-hint" style={{ gridColumn: "1/-1" }}>
              Only the Invoice and Delivery Order numbers can be corrected. Stock, cost, and the
              receiving date are never changed.
            </p>
            <label className="field" style={{ gridColumn: "1/-1" }}>
              <span>Receiving date (locked — follows original receipt)</span>
              <input data-testid="grn-doc-received-readonly" value={formatDate(docEdit.received_at)} readOnly style={{ background: "#f6f8f6", cursor: "not-allowed" }} />
            </label>
            <Field label="Invoice number" testid="grn-edit-invoice-input" value={invNo} onChange={setInvNo} placeholder="e.g. INV-2026-0012" />
            <Field label="Delivery Order number" testid="grn-edit-do-input" value={doNo} onChange={setDoNo} placeholder="e.g. DO-8891" />
            {(docEdit.edit_history || []).length > 0 && (
              <div style={{ gridColumn: "1/-1" }}>
                <p className="eyebrow">Edit history</p>
                <ul className="attach-list" data-testid="grn-edit-history">
                  {docEdit.edit_history.map((h, i) => (
                    <li key={i}>
                      <FileText size={14} /> {h.field}: <b>{h.old || "-"}</b> → <b>{h.new || "-"}</b>
                      <small>{h.by} · {formatDate(h.at)}</small>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="form-actions">
              <button type="button" className="secondary-button" onClick={() => setDocEdit(null)}>Cancel</button>
              <button type="submit" data-testid="grn-doc-save-button" className="primary-button" disabled={savingDoc}>
                <Check size={16} /> {savingDoc ? "Saving..." : "Save numbers"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {modal === "upload" && (
        <BulkUploadDialog
          title="Upload receivings (CSV)"
          endpoint="/receivings/bulk-upload"
          templateName="receivings_template.csv"
          templateHeaders={TEMPLATE.headers}
          templateExample={TEMPLATE.example}
          instructions={
            <>
              Must include <b>po_number</b> that has status <i>approved</i>. SKU in the
              CSV must match items on the PO. Weighted-average COGS is auto-updated.
            </>
          }
          onClose={() => setModal(null)}
          onSuccess={() => {
            loadList();
            loadPOs();
          }}
          testid="receivings-upload"
        />
      )}
    </>
  );
}
